import { getUser, getSelectedClass } from '@/utils/auth'
import { getClassSlotKey, getSandSession } from '@/utils/sandSession'
import {
  isAdultSandUser,
  normalizeEducationLabel,
  resolveUserTypeCode
} from '@/utils/sandTableRestore'

const SAND_CATEGORY_MAP = {
  character: { num: '01', name: '人物' },
  animal: { num: '02', name: '动物' },
  plant: { num: '03', name: '植物' },
  artificial: { num: '04', name: '人造' },
  transport: { num: '05', name: '交通' }
}

const THEME_TYPE_MAP = {
  人物: '人物主题',
  动物: '动物主题',
  植物: '自然主题',
  交通: '交通主题',
  人造: '建筑主题'
}

function buildThemeType (modeData, activeWeatherId) {
  const buckets = {}
  modeData.forEach((item) => {
    const typeName = item.typeName || '人物'
    buckets[typeName] = (buckets[typeName] || 0) + 1
  })
  const themeMap = {
    人物主题: 0,
    自然主题: 0,
    动物主题: 0,
    建筑主题: 0,
    交通主题: 0,
    气候主题: 0
  }
  Object.entries(buckets).forEach(([typeName, count]) => {
    const themeName = THEME_TYPE_MAP[typeName] || '建筑主题'
    themeMap[themeName] = (themeMap[themeName] || 0) + count
  })
  if (activeWeatherId && activeWeatherId !== 'day-clear') {
    themeMap['气候主题'] = 1
  }
  return Object.entries(themeMap).map(([themeName, themeNum]) => ({
    themeName,
    themeNum: String(themeNum)
  }))
}

/**
 * 与 psychology_info / 用户信息表字段长度对齐
 * planform varchar(20~25)  thumbnail varchar(255)  time varchar(10)
 * user_type char(2)  status_data varchar(2)  mode char(4)
 */
const FIELD_LIMIT = {
  planform: 255,
  thumbnail: 255,
  time: 10,
  slot: 25,
  mode: 4,
  finish: 10,
  userType: 2,
  statusData: 2,
  codingTime: 20,
  lakesRatio: 60,
  mountainRatio: 60,
  codeInfos: 200,
  reviewPic: 200,
  fileName: 255,
  name: 60,
  schoolName: 200,
  className: 200,
  sex: 10,
  age: 10,
  depressionScore: 100,
  projectScores: 255,
  gadTable: 200,
  phqTable: 200,
  reportName: 200,
  reportAge: 200,
  reportSex: 200,
  reportEdc: 200,
  reportMarital: 200,
  reportGrade: 200,
  reportClass: 200
}

function clipEducation (value, maxLen) {
  return clip(normalizeEducationLabel(value), maxLen)
}

function clip (value, maxLen) {
  if (value == null || value === '') return ''
  const str = String(value)
  return str.length > maxLen ? str.slice(0, maxLen) : str
}

/** 图片字段只存短文件名，拒绝 base64 */
function clipImageFileName (value, maxLen) {
  if (!value || typeof value !== 'string') return ''
  if (value.indexOf('data:image') === 0) return ''
  const name = value.split('/').pop() || value
  return clip(name, maxLen)
}

function buildPlanformFileName (now) {
  return clip(
    'sp' +
      String(now.getFullYear()).slice(2) +
      padDate(now.getMonth() + 1) +
      padDate(now.getDate()) +
      padDate(now.getHours()) +
      padDate(now.getMinutes()) +
      '.jpg',
    FIELD_LIMIT.planform
  )
}

/** fileName 格式：{workName}*{YYYY/M/D HH:mm:ss}，超长截断保留后缀时间部分 */
function buildFileName (workName, now) {
  const stamp =
    now.getFullYear() + '/' +
    (now.getMonth() + 1) + '/' +
    now.getDate() + ' ' +
    padDate(now.getHours()) + ':' +
    padDate(now.getMinutes()) + ':' +
    padDate(now.getSeconds())
  const safeName = clip(workName || '我的沙盘作品', FIELD_LIMIT.fileName - stamp.length - 1)
  return clip(safeName + '*' + stamp, FIELD_LIMIT.fileName)
}

function formatElapsedTime (totalSec) {
  const sec = Math.max(0, Math.floor(totalSec))
  const m = Math.floor(sec / 60)
  const s = sec % 60
  const text = m + "'" + String(s).padStart(2, '0') + '"'
  if (text.length <= FIELD_LIMIT.time) return text
  return clip(String(m) + ':' + padDate(s), FIELD_LIMIT.time)
}

function padDate (n) {
  return n < 10 ? '0' + n : String(n)
}

/**
 * 业务状态码规则表（完整版）— Cursor Write It
 *
 * 01xxx 地表地面
 * 02xxx 天空天气
 * 03xxx 沙具数量/分布
 * 04xxx 建筑
 * 05xxx 人物
 * 06xxx 交通
 * 07xxx 植物
 * 08xxx 特殊沙具（组合）
 * 09xxx 沙盘区域分布
 * 10xxx 左右区域
 * 11xxx 左上角（童年记忆）
 * 12xxx 右上角（未来）
 * 13xxx 中上/中中/中下区域
 * 14xxx 细分类
 */

// ---------- 天气码映射 ----------
const WEATHER_CODE_MAP = {
  'day-rain': '02001',
  'night-rain': '02001',
  'day-snow': '02002',
  'night-snow': '02002',
  'night-clear': '02003',
  'night-cloudy': '02003',
  'night-rain': '02004',
  'night-snow': '02005'
}

// ---------- 特殊沙具关键词匹配 ----------
// key = code, value = [沙具 label 或 key 的包含关键词]
const SPECIAL_PROP_CODE_MAP = {
  '08001': ['蝴蝶', '荷花', '圣诞树'],
  '08002': ['兔子'],
  '08003': ['太阳花', '向日葵', '圣诞树'],
  '08004': ['火山'],
  '08005': ['望远镜', '灯塔'],
  '08006': ['警察局', '消防局', '狗'],
  '08007': ['冰屋', '帐篷'],
  '08008': ['电影院', 'KTV'],
  '08009': ['白无常', '黑无常'],
  '08010': ['金字塔', '狮身人面像'],
  '08012': ['父', '儿子'],
  '08013': ['母', '女儿']
}

// 高权重人物/动物沙具（targetHeight ≥ 150 的等比高度，约对应权重 ≥ 150）
// 扩展：需在 manifest 中标注 weight 字段；前端用 targetHeight 折算
const HIGH_WEIGHT_THRESHOLD = 150

// ---------- 辅助：判定连通簇数（简化为阈值法）----------
const SCULPT_MAX_Y = 0.58
const MOUNTAIN_H_THRESHOLD = SCULPT_MAX_Y * 0.2 // ~10米高度对应阈值
const THREE_MOUNTAIN_H_THRESHOLD = SCULPT_MAX_Y * 0.35 // ~3座以上连通簇

// ---------- 辅助：按 label / key 匹配关键词 ----------
function propMatchesAnyKeyword (label, keywords) {
  if (!label) return false
  const s = String(label)
  return keywords.some((kw) => s.includes(kw))
}

function propsByKeyword (props, keywords) {
  return props.filter((p) => propMatchesAnyKeyword(p.label, keywords))
}

// ---------- 辅助：从 terrainData / terrainWet / terrainMat 中解析比例 ----------
function computeTerrainTypeRatio (terrainData, terrainMat, typeIndex) {
  // terrainData 是非零顶点稀疏表；用 (n+1)^2 近似总顶点数
  // 但更可靠的做法是直接统计 terrainMat 里指定 typeIndex 的比例
  if (!terrainMat || !terrainMat.length) return 0
  const total = terrainData && terrainData.length > 0 ? (terrainData.length) : 1
  const matched = terrainMat.filter((pt) => parseInt(pt.m, 10) === typeIndex).length
  // terrainMat.length 是带材质标记的顶点数；用 terrainData 总顶点数 (segX+1)*(segZ+1)
  // 这里用 terrainData 长度估算（不精确但可行）
  const estimatedTotal = terrainData && terrainData.length > 0
    ? terrainData.length + (terrainData.length > 100 ? 0 : 0) // 已是稀疏表，matched/total 直接比
    : 1
  return matched / Math.max(1, estimatedTotal)
}

// ---------- 辅助：提取所有沙具属性（统一入口） ----------
function extractSandProps (sceneData) {
  const allModelData = sceneData.allModelData || []
  const modeData = sceneData.modeData || []
  // allModelData 有 modelId/modelType/modelPower/modelDetails
  // modeData 有 num/name/typeName/pos/rota/scale 等
  // 合并：以 modeData 为主（含 position），allModelData 做补充
  const props = []
  const amap = {}
  allModelData.forEach((m) => { amap[m.modelId] = m })
  modeData.forEach((row) => {
    const modelId = row.modelId || 'model_' + String(row.num || '').padStart(3, '0')
    const am = amap[modelId] || {}
    const label = row.name || am.modelDetails || ''
    const typeName = row.typeName || ''
    const category = row.typeNum === '01' ? 'character'
      : row.typeNum === '02' ? 'animal'
      : row.typeNum === '03' ? 'plant'
      : row.typeNum === '04' ? 'artificial'
      : row.typeNum === '05' ? 'transport' : ''
    props.push({
      label,
      typeName,
      category,
      typeNum: row.typeNum || am.modelType || '01',
      modelPower: parseInt(am.modelPower, 10) || 80,
      scale: parseFloat(row.scale) || 1,
      pos: row.pos || { x: '0', y: '0', z: '0' }
    })
  })
  return props
}

// ---------- 辅助：计算指定类别沙具的数量 ----------
function countByCategory (props, category) {
  return props.filter((p) => p.category === category).length
}

function countByTypeNum (props, typeNum) {
  return props.filter((p) => p.typeNum === typeNum).length
}

// ---------- 辅助：按 category 统计 props ----------
function groupByCategory (props) {
  const g = {}
  props.forEach((p) => {
    const cat = p.category || 'other'
    if (!g[cat]) g[cat] = []
    g[cat].push(p)
  })
  return g
}

// ---------- 辅助：计算平均距离 ----------
function averageDistance (props) {
  if (props.length < 2) return Infinity
  let sum = 0
  let count = 0
  for (let i = 0; i < props.length; i++) {
    for (let j = i + 1; j < props.length; j++) {
      const dx = parseFloat(props[i].pos.x) - parseFloat(props[j].pos.x)
      const dz = parseFloat(props[i].pos.z) - parseFloat(props[j].pos.z)
      sum += Math.sqrt(dx * dx + dz * dz)
      count++
    }
  }
  return count > 0 ? sum / count : Infinity
}

// ---------- 辅助：判定是否在特定区域 ----------
function inRegion (x, z, region) {
  // 沙盘 X/Z 范围约 ±8 单位（sandboxRoot / ISLAND_HALF_X）
  const IX = 8
  const IZ = 8
  const px = parseFloat(x)
  const pz = parseFloat(z)
  switch (region) {
    case 'left': return px < -IX * 0.33
    case 'right': return px > IX * 0.33
    case 'top': return pz < -IZ * 0.33   // Z 负向为"上/北"
    case 'bottom': return pz > IZ * 0.33 // Z 正向为"下/南"
    case 'center': return Math.abs(px) <= IX * 0.33 && Math.abs(pz) <= IZ * 0.33
    case 'leftTop': return px < -IX * 0.33 && pz < -IZ * 0.33
    case 'rightTop': return px > IX * 0.33 && pz < -IZ * 0.33
    case 'centerTop': return Math.abs(px) <= IX * 0.33 && pz < -IZ * 0.33
    case 'centerMid': return Math.abs(px) <= IX * 0.33 && Math.abs(pz) <= IZ * 0.33
    case 'centerBottom': return Math.abs(px) <= IX * 0.33 && pz > IZ * 0.33
    default: return false
  }
}

function propsInRegion (props, region) {
  return props.filter((p) => inRegion(p.pos.x, p.pos.z, region))
}

// ---------- 辅助：检查连通簇（简化：用 terrainData 高顶点比例近似） ----------
function countMountainClusters (sceneData) {
  // 用 mountainRatio 反推：mountainRatio > 0.05 约等于有山
  // 精确连通簇数较复杂，此处用 terrainData 高顶点分布近似
  // sceneData.terrainData 含每个高顶点 {h: ..., n: "ix_iz"}
  // 将高顶点（h > MOUNTAIN_H_THRESHOLD）的 ix/iz 用简单连通域染色
  const td = sceneData.terrainData || []
  const highPts = new Map()
  td.forEach((pt) => {
    const h = parseFloat(pt.h)
    if (h > THREE_MOUNTAIN_H_THRESHOLD) {
      highPts.set(pt.n, true)
    }
  })
  if (highPts.size === 0) return 0
  const visited = new Set()
  let clusters = 0
  highPts.forEach((_, key) => {
    if (!visited.has(key)) {
      clusters++
      const queue = [key]
      while (queue.length) {
        const cur = queue.shift()
        if (visited.has(cur)) continue
        visited.add(cur)
        const [cx, cz] = cur.split('_').map(Number)
        ;[
          [cx - 1, cz], [cx + 1, cz],
          [cx, cz - 1], [cx, cz + 1]
        ].forEach(([nx, nz]) => {
          const nk = nx + '_' + nz
          if (highPts.has(nk) && !visited.has(nk)) queue.push(nk)
        })
      }
    }
  })
  return clusters
}

// ---------- 主函数 ----------
function buildCodeInfosString (sceneData, activeWeatherId) {
  const codes = []
  const props = extractSandProps(sceneData)
  const flatRatio = typeof sceneData.flatRatio === 'number' ? sceneData.flatRatio : -1
  const lakesRatio = typeof sceneData.lakesRatio === 'number'
    ? sceneData.lakesRatio
    : parseFloat(sceneData.lakesRatio) || 0
  const mountainRatio = typeof sceneData.mountainRatio === 'number'
    ? sceneData.mountainRatio
    : parseFloat(sceneData.mountainRatio) || 0

  // ---- 01xxx 地表地面 ----
  // 01005: 超过10米高山（高顶点比例 > 某阈值）
  if (mountainRatio > 0.08) codes.push('01005')
  // 01006: 平整0%-20%（flatRatio 0~0.2）
  if (flatRatio >= 0 && flatRatio <= 0.2) codes.push('01006')
  // 01007: 2座超过10米高山
  const clusters2 = countMountainClusters(sceneData)
  if (clusters2 >= 2) codes.push('01007')
  // 01008: 地面3面高山
  if (clusters2 >= 3) codes.push('01008')
  // 01009: 地面4面高山
  if (clusters2 >= 4) codes.push('01009')
  // 01010: 水体50%以上
  if (lakesRatio >= 0.5) codes.push('01010')
  // 01011: 水体70%以上
  if (lakesRatio >= 0.7) codes.push('01011')
  // 材质比例（从 terrainMat 提取）
  const terrainMat = sceneData.code ? (function () {
    try {
      const c = typeof sceneData.code === 'string' ? JSON.parse(sceneData.code) : sceneData.code
      return c.terrainMat || []
    } catch (err) { return [] }
  })() : []
  // 01012: 草地60%以上 (typeIndex 0)
  if (computeTerrainTypeRatio(sceneData.terrainData, terrainMat, 0) >= 0.6) codes.push('01012')
  // 01013: 雪地90%以上 (typeIndex 2)
  if (computeTerrainTypeRatio(sceneData.terrainData, terrainMat, 2) >= 0.9) codes.push('01013')
  // 01014: 沙漠90%以上 (typeIndex 1)
  if (computeTerrainTypeRatio(sceneData.terrainData, terrainMat, 1) >= 0.9) codes.push('01014')
  // 01015: 黑砖90%以上 (typeIndex 3)
  if (computeTerrainTypeRatio(sceneData.terrainData, terrainMat, 3) >= 0.9) codes.push('01015')
  // 01016: 地表类型≥4种（terrainMat 中不同 m 值的数量）
  const uniqueMats = new Set(terrainMat.map((pt) => pt.m).filter(Boolean))
  if (uniqueMats.size >= 4) codes.push('01016')
  // 保留原有平整度码（01001-01004，与01006互斥）
  if (flatRatio >= 0.8) codes.push('01001')
  else if (flatRatio >= 0.6) codes.push('01002')
  else if (flatRatio >= 0.4) codes.push('01003')
  else if (flatRatio >= 0 && flatRatio < 0.4) codes.push('01004')

  // ---- 02xxx 天空天气 ----
  const weatherCode = WEATHER_CODE_MAP[activeWeatherId]
  if (weatherCode) codes.push(weatherCode)

  // ---- 03xxx 沙具数量/分布 ----
  const propCount = props.length
  if (propCount < 5) codes.push('03001')
  if (propCount < 15) codes.push('03002')
  if (propCount > 30) codes.push('03003')
  if (propCount > 15) codes.push('03004')
  // 03005: 沙具总量>15 且集中连续3个区域范围
  if (propCount > 15) {
    const regionKeys = ['leftTop', 'centerTop', 'rightTop', 'centerMid', 'centerBottom']
    const regionCounts = regionKeys.map((rk) => propsInRegion(props, rk).length).filter((c) => c > 0)
    if (regionCounts.length <= 3 && Math.max(...regionCounts, 0) > propCount * 0.5) {
      codes.push('03005')
    }
  }
  // 03006: 高大建筑中心点在边沿
  const buildingProps = props.filter(
    (p) => p.category === 'artificial' &&
      /楼|塔|厦|金字塔|体育场|体育馆/i.test(p.label)
  )
  buildingProps.forEach((bp) => {
    if (Math.abs(parseFloat(bp.pos.x)) > 6 || Math.abs(parseFloat(bp.pos.z)) > 6) {
      codes.push('03006')
    }
  })
  // 03007-03009: 低权重人物/动物放大
  const lowWeightCharAni = props.filter(
    (p) => (p.category === 'character' || p.category === 'animal') &&
      (p.modelPower < HIGH_WEIGHT_THRESHOLD)
  )
  lowWeightCharAni.forEach((p) => {
    if (p.scale > 1.5) codes.push('03008') // 放大50%以上
    if (p.scale > 1.8) codes.push('03009') // 放大80%以上
  })
  if (lowWeightCharAni.length > 0) codes.push('03007')

  // ---- 04xxx 建筑 ----
  const buildings = props.filter((p) => p.category === 'artificial')
  if (buildings.length === 0) codes.push('04001')
  if (buildings.length >= 2) {
    const avgDist = averageDistance(buildings)
    if (avgDist < 5) codes.push('04002')
    if (avgDist < 10) codes.push('04003')
  }
  if (buildings.length >= 3) {
    const xSorted = [...buildings].sort((a, b) => parseFloat(a.pos.x) - parseFloat(b.pos.x))
    const zSorted = [...buildings].sort((a, b) => parseFloat(a.pos.z) - parseFloat(b.pos.z))
    const xGap = Math.abs(parseFloat(xSorted[xSorted.length - 1].pos.x) - parseFloat(xSorted[0].pos.x))
    const zGap = Math.abs(parseFloat(zSorted[zSorted.length - 1].pos.z) - parseFloat(zSorted[0].pos.z))
    // 整齐：间距均匀（标准差小）且非随机分布
    const isTidy = xGap > 2 && zGap > 2
    if (isTidy) codes.push('04004')
    else codes.push('04005')
  }
  // 04006-04007: 建筑物平均落差（Y 方向，height 或 scale 估算）
  if (buildings.length >= 2) {
    const heights = buildings.map((b) => parseFloat(b.pos.y) || 0)
    const avgH = heights.reduce((a, b) => a + b, 0) / heights.length
    const maxDiff = Math.max(...heights.map((h) => Math.abs(h - avgH)))
    if (maxDiff > 10) codes.push('04006')
    if (maxDiff > 20) codes.push('04007')
  }

  // ---- 05xxx 人物 ----
  const characters = props.filter((p) => p.category === 'character')
  if (characters.length > 20) codes.push('05001')
  if (characters.length < 1) codes.push('05002')
  if (characters.length < 5) codes.push('05003')
  if (characters.length >= 2) {
    const charAvgDist = averageDistance(characters)
    if (charAvgDist < 10) codes.push('05004')
    if (charAvgDist < 5) codes.push('05005')
    if (charAvgDist > 15) codes.push('05006')
  }

  // ---- 06xxx 交通 ----
  const transports = props.filter((p) => p.category === 'transport')
  const hasVehicle = transports.some((p) => /车|汽车|公交|卡/i.test(p.label))
  const hasPlane = transports.some((p) => /飞机|直升机|飞艇/i.test(p.label))
  const hasShip = transports.some((p) => /船|舰|艇/i.test(p.label))
  const shipOnLand = transports.filter(
    (p) => /船|舰|艇/i.test(p.label) && parseFloat(p.pos.y) > 0.05
  )
  const shipUnderWater = transports.filter(
    (p) => /船|舰|艇/i.test(p.label) && parseFloat(p.pos.y) <= 0.05
  )
  if (hasVehicle) codes.push('06001')
  if (hasVehicle) {
    const rotSet = new Set(transports.filter((p) => /车|汽车/i.test(p.label)).map((p) => {
      const ry = parseFloat(p.pos.r || 0)
      return Math.round(ry / 90) * 90
    }))
    if (rotSet.size >= 2) codes.push('06002')
  }
  if (hasPlane) codes.push('06003')
  if (shipOnLand.length > 0) codes.push('06004')
  if (shipUnderWater.length > 0) codes.push('06005')

  // ---- 07xxx 植物 ----
  const plants = props.filter((p) => p.category === 'plant')
  // 高权重植物：目前用 modelPower 估算；需 manifest 加 weight 字段
  const highWeightPlants = plants.filter((p) => p.modelPower >= HIGH_WEIGHT_THRESHOLD)
  if (highWeightPlants.length <= 3 && highWeightPlants.length > 0) codes.push('07001')
  if (highWeightPlants.length <= 10 && highWeightPlants.length > 0) codes.push('07002')
  if (highWeightPlants.length > 10) codes.push('07003')
  if (plants.length === 0) codes.push('07004')

  // ---- 08xxx 特殊沙具 ----
  Object.entries(SPECIAL_PROP_CODE_MAP).forEach(([code, keywords]) => {
    if (propsByKeyword(props, keywords).length > 0) codes.push(code)
  })
  // 08011: 金字塔、狮身人面像同时出现，且金字塔盖住/压住狮身人面像
  const hasPyramid = propsByKeyword(props, ['金字塔']).length > 0
  const hasSphinx = propsByKeyword(props, ['狮身人面像']).length > 0
  if (hasPyramid && hasSphinx) {
    const pyramidProp = props.find((p) => /金字塔/i.test(p.label))
    const sphinxProp = props.find((p) => /狮身人面像/i.test(p.label))
    if (pyramidProp && sphinxProp) {
      const dx = Math.abs(parseFloat(pyramidProp.pos.x) - parseFloat(sphinxProp.pos.x))
      const dz = Math.abs(parseFloat(pyramidProp.pos.z) - parseFloat(sphinxProp.pos.z))
      if (dx < 2 && dz < 2) codes.push('08011')
    }
  }
  // 08014: 父子、母子同时出现，距离<10
  const hasFatherSon = propsByKeyword(props, ['父', '儿子']).length >= 2
  const hasMotherChild = propsByKeyword(props, ['母', '女儿']).length >= 2
  if (hasFatherSon && hasMotherChild) {
    const father = props.find((p) => /父|爸/i.test(p.label))
    const mother = props.find((p) => /母|妈/i.test(p.label))
    if (father && mother) {
      const dx = parseFloat(father.pos.x) - parseFloat(mother.pos.x)
      const dz = parseFloat(father.pos.z) - parseFloat(mother.pos.z)
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist < 10) codes.push('08014')
      if (dist >= 10) codes.push('08015')
    }
  }
  // 08016: 唐僧师徒2个以上同时出现
  const masterDisciples = propsByKeyword(props, ['大圣', '八戒', '唐僧', '沙僧', '白龙马'])
  if (masterDisciples.length >= 2) codes.push('08016')
  // 08017: 唐僧师徒平均距离<5
  if (masterDisciples.length >= 2) {
    const avgDist = averageDistance(masterDisciples)
    if (avgDist < 5) codes.push('08017')
  }
  // 08018: 孙悟空模型大于唐僧（scale）
  const wukong = props.find((p) => /大圣|孙悟空|悟空/i.test(p.label))
  const tangseng = props.find((p) => /唐僧|三藏/i.test(p.label))
  if (wukong && tangseng && wukong.scale > tangseng.scale) codes.push('08018')
  // 08019: 高山与城市；古代与现代；西方与东方；冰屋与绿地
  const hasMountain = mountainRatio > 0.1
  const hasCity = buildings.some((p) => /楼|厦|城/i.test(p.label))
  const hasAncient = props.some((p) => /古代|古建筑|佛像/i.test(p.label))
  const hasModern = props.some((p) => /汽车|公交|电视|电脑/i.test(p.label))
  const hasWestern = buildings.some((p) => /教堂|塔|欧式/i.test(p.label))
  const hasEastern = buildings.some((p) => /庙|塔|中式|亭/i.test(p.label))
  const hasIceHouse = propsByKeyword(props, ['冰屋']).length > 0
  const hasGreenPlant = plants.filter((p) => p.modelPower < 200).length > 0
  if (
    (hasMountain && hasCity) || (hasAncient && hasModern) ||
    (hasWestern && hasEastern) || (hasIceHouse && hasGreenPlant)
  ) codes.push('08019')
  // 08020: 高山(>15米)与湖泊相近(<5米)
  const hasLake = lakesRatio > 0.05
  const highMountain = mountainRatio > 0.2
  if (hasLake && highMountain) codes.push('08020')
  // 08021: 矮房子
  const lowHouses = props.filter(
    (p) => p.category === 'artificial' && /矮|平房|小屋/i.test(p.label)
  )
  if (lowHouses.length > 0) codes.push('08021')
  // 08022: 体育场
  if (props.some((p) => /体育场|体育馆|运动场/i.test(p.label))) codes.push('08022')
  // 08023-08024: 战士
  const warriors = props.filter((p) => /战士|将军|士兵|将军/i.test(p.label))
  if (warriors.length > 0) codes.push('08023')
  if (warriors.length > 5) codes.push('08024')
  // 08025: 矮房子、父子、母子-都没有（缺失）
  if (lowHouses.length === 0 &&
      propsByKeyword(props, ['父', '儿子']).length === 0 &&
      propsByKeyword(props, ['母', '女儿']).length === 0) {
    codes.push('08025')
  }

  // ---- 09xxx 沙盘区域分布 ----
  const topProps = propsInRegion(props, 'centerTop')
  const midProps = propsInRegion(props, 'centerMid')
  const bottomProps = propsInRegion(props, 'centerBottom')
  const regionCount = [topProps, midProps, bottomProps].filter((r) => r.length > 0).length
  if (regionCount === 3) codes.push('09001')
  else if (regionCount === 2) codes.push('09002')
  else if (regionCount === 1) codes.push('09003')
  else if (regionCount === 0) codes.push('09004')

  // ---- 10xxx 左右区域 ----
  const leftBuildings = buildings.filter((p) => parseFloat(p.pos.x) < 0)
  const rightBuildings = buildings.filter((p) => parseFloat(p.pos.x) > 0)
  const highLeft = leftBuildings.filter((p) => p.scale > 1.5 || /楼|塔/i.test(p.label))
  const highRight = rightBuildings.filter((p) => p.scale > 1.5 || /楼|塔/i.test(p.label))
  const allBuildings = buildings.filter((p) => p.scale > 1.5 || /楼|塔|山/i.test(p.label))
  const allLeft = allBuildings.every((p) => parseFloat(p.pos.x) < 0)
  const allRight = allBuildings.every((p) => parseFloat(p.pos.x) > 0)
  const leftHigh = allBuildings.every((p) => parseFloat(p.pos.x) < 0)
  const rightHigh = allBuildings.every((p) => parseFloat(p.pos.x) > 0)
  if (leftHigh && rightHigh) codes.push('10001')
  if (rightHigh && !leftHigh) codes.push('10002')
  if (leftHigh && !rightHigh) codes.push('10003')

  // ---- 11xxx 左上角（童年记忆）----
  const leftTopProps = propsInRegion(props, 'leftTop')
  if (leftTopProps.some((p) => p.modelPower >= HIGH_WEIGHT_THRESHOLD)) codes.push('11001')
  if (leftTopProps.some((p) => p.category === 'plant' || /绿|草|树/i.test(p.label))) codes.push('11002')
  if (leftTopProps.some((p) => /矮|平房|小屋/i.test(p.label))) codes.push('11003')
  if (leftTopProps.some((p) => mountainRatio > 0.15)) codes.push('11004')
  if (leftTopProps.some((p) => /冰屋|枯树/i.test(p.label))) codes.push('11005')
  if (leftTopProps.some((p) => p.category === 'plant')) {
    const snowRatio = computeTerrainTypeRatio(sceneData.terrainData, terrainMat, 2)
    if (snowRatio > 0.5) codes.push('11006')
  }
  if (leftTopProps.length === 0) codes.push('11007')

  // ---- 12xxx 右上角（未来）----
  const rightTopProps = propsInRegion(props, 'rightTop')
  if (rightTopProps.some((p) => /楼|厦|高/i.test(p.label))) codes.push('12001')
  if (rightTopProps.some((p) => p.category === 'artificial' && !/楼|厦|高/i.test(p.label))) codes.push('12002')
  if (rightTopProps.length === 0) codes.push('12003')

  // ---- 13xxx 中上/中中/中下区域 ----
  const cTopProps = propsInRegion(props, 'centerTop')
  const cMidProps = propsInRegion(props, 'centerMid')
  const cBottomProps = propsInRegion(props, 'centerBottom')
  const centerAllProps = [...cTopProps, ...cMidProps, ...cBottomProps]
  const lowWeightCenter = centerAllProps.filter(
    (p) => (p.category === 'character' || p.category === 'animal') &&
      p.modelPower < HIGH_WEIGHT_THRESHOLD
  )
  const highWeightCenter = centerAllProps.filter(
    (p) => (p.category === 'character' || p.category === 'animal') &&
      p.modelPower >= HIGH_WEIGHT_THRESHOLD
  )
  const expandedLow = lowWeightCenter.filter((p) => p.scale > 1.5)
  const expandedHigh = highWeightCenter.filter((p) => p.scale > 1.5)
  if (expandedHigh.length > 0) codes.push('13001')
  if (centerAllProps.some((p) => p.category === 'artificial' && /楼|厦/i.test(p.label))) codes.push('13002')
  if (expandedLow.length > 0) codes.push('13003')

  // ---- 14xxx 细分类 ----
  const uniqueTypes = new Set(props.map((p) => p.typeNum).filter(Boolean))
  if (uniqueTypes.size < 5) codes.push('14001')
  if (uniqueTypes.size > 20) codes.push('14002')

  // 去重 + 排序输出
  return [...new Set(codes)].sort().join(',')
}

function buildCodeWithUserType (sceneData, session) {
  let codeObj = {}
  try {
    const raw = sceneData && sceneData.code
    if (raw) {
      codeObj = typeof raw === 'string' ? JSON.parse(raw) : raw
    }
  } catch (e) {
    codeObj = {}
  }
  if (!codeObj || typeof codeObj !== 'object') codeObj = {}

  if (isAdultSandUser(session) && session.userType) {
    codeObj.userType = resolveUserTypeCode(session.userType)
  } else {
    delete codeObj.userType
  }
  return JSON.stringify(codeObj)
}

function buildModeTimeStatis (modeData) {
  const buckets = {}
  modeData.forEach((item) => {
    const key = item.typeNum
    if (!buckets[key]) {
      buckets[key] = {
        num: key,
        name: item.typeName,
        increase: 0,
        decrease: 0,
        sum: 0
      }
    }
    buckets[key].sum += 1
    buckets[key].increase += 1
  })
  const list = Object.keys(buckets)
    .sort()
    .map((key) => buckets[key])
  const total = modeData.length
  if (total > 0) {
    list.push({
      num: '20',
      name: '总数',
      increase: total,
      decrease: 0,
      sum: total
    })
  }
  return list
}

function buildModeQuadrantStatis (modeData) {
  const quadrants = {
    Q1: { quadrant: 'Q1', type: {} },
    Q2: { quadrant: 'Q2', type: {} },
    Q3: { quadrant: 'Q3', type: {} },
    Q4: { quadrant: 'Q4', type: {} }
  }
  modeData.forEach((item) => {
    const x = parseFloat(item.pos.x)
    const z = parseFloat(item.pos.z)
    const q =
      x >= 0 && z >= 0
        ? 'Q1'
        : x < 0 && z >= 0
          ? 'Q2'
          : x < 0 && z < 0
            ? 'Q3'
            : 'Q4'
    const key = item.typeNum
    if (!quadrants[q].type[key]) {
      quadrants[q].type[key] = {
        num: key,
        name: item.typeName,
        count: 0
      }
    }
    quadrants[q].type[key].count += 1
  })
  return Object.keys(quadrants)
    .map((key) => ({
      quadrant: quadrants[key].quadrant,
      type: Object.keys(quadrants[key].type)
        .sort()
        .map((typeKey) => quadrants[key].type[typeKey])
    }))
    .filter((entry) => entry.type.length > 0)
}

function buildModelStatistics (modeData) {
  if (!modeData.length) return []
  const buckets = {}
  modeData.forEach((item) => {
    if (!buckets[item.typeName]) {
      buckets[item.typeName] = { count: 0, time: 0 }
    }
    buckets[item.typeName].count += 1
    buckets[item.typeName].time += item.timeInterval || 0
  })
  const totalCount = modeData.length
  const totalTime = modeData.reduce((sum, item) => sum + (item.timeInterval || 0), 0) || 1
  return Object.keys(buckets).map((typeName) => {
    const bucket = buckets[typeName]
    return {
      typeName,
      typeRatio: Math.round((bucket.count / totalCount) * 100) + '%',
      timeRatio: Math.round((bucket.time / totalTime) * 100) + '%'
    }
  })
}

/**
 * 将场景导出数据与页面上下文组装为 PsychologyInfoRequest
 */
export function buildSandTablePayload ({
  sceneData = {},
  activeWeatherId = 'day-clear',
  elapsedSec = 0,
  gadTable = null,
  phqTable = null
}) {
  const user = getUser() || {}
  const session = getSandSession() || {}
  const selectedClass = getSelectedClass() || {}
  const modeData = sceneData.modeData || []
  const allModelData = sceneData.allModelData || []
  const terrainData = sceneData.terrainData || []
  const now = new Date()
  const workName = session.workName || '我的沙盘作品'
  const planformName =
    (sceneData.planform && !sceneData.planform.startsWith('data:image'))
      ? clip(sceneData.planform, FIELD_LIMIT.planform)
      : buildPlanformFileName(now)
  const reviewPicUrl =
    (sceneData.reviewPic && !sceneData.reviewPic.startsWith('data:image'))
      ? clip(sceneData.reviewPic, FIELD_LIMIT.reviewPic)
      : ''
  const fileName = buildFileName(workName, now)

  const genderLabel = session.gender === 'female' ? '女' : '男'
  const eduLabel = clipEducation(session.education, FIELD_LIMIT.reportEdc)
  const codeInfos = clip(
    buildCodeInfosString(sceneData, activeWeatherId),
    FIELD_LIMIT.codeInfos
  )

  return {
    id: null,
    userId: user.userId,
    name: clip(session.name || user.account || '', FIELD_LIMIT.name),
    mode: clip('1', FIELD_LIMIT.mode),
    age: clip(session.age != null ? String(session.age) : '', FIELD_LIMIT.age),
    schoolName: clip(
      selectedClass.schoolName || selectedClass.className || '',
      FIELD_LIMIT.schoolName
    ),
    className: clip(selectedClass.className || '', FIELD_LIMIT.className),
    slot: clip(
      session.slot || selectedClass.slot || getClassSlotKey(selectedClass, 0),
      FIELD_LIMIT.slot
    ),
    code: buildCodeWithUserType(sceneData, session),
    time: formatElapsedTime(elapsedSec),
    finish: clip('1', FIELD_LIMIT.finish),
    planform: planformName,
    lakesRatio: clip(sceneData.lakesRatio || '0', FIELD_LIMIT.lakesRatio),
    reviewPic: reviewPicUrl,
    codeInfos,
    projectScores: '',
    mountainRatio: clip(sceneData.mountainRatio || '0', FIELD_LIMIT.mountainRatio),
    codingTime: clip(
      String(Math.max(0, Math.floor(elapsedSec))),
      FIELD_LIMIT.codingTime
    ),
    userType: clip(
      isAdultSandUser(session) && session.userType
        ? resolveUserTypeCode(session.userType)
        : '1',
      FIELD_LIMIT.userType
    ),
    depressionScore: '',
    statusData: clip('0', FIELD_LIMIT.statusData),
    thumbnail:
      (sceneData.thumbnail && !sceneData.thumbnail.startsWith('data:image'))
        ? clip(sceneData.thumbnail, FIELD_LIMIT.thumbnail)
        : clip(planformName, FIELD_LIMIT.thumbnail),
    fileName,
    reportName: clip(session.name || user.account || '', FIELD_LIMIT.reportName),
    reportAge: clip(session.age != null ? String(session.age) : '', FIELD_LIMIT.reportAge),
    reportSex: clip(genderLabel, FIELD_LIMIT.reportSex),
    reportEdc: eduLabel,
    reportMarital: clip(session.marital || '未婚', FIELD_LIMIT.reportMarital),
    reportGrade: clip(session.grade || '', FIELD_LIMIT.reportGrade),
    reportClass: clip(selectedClass.className || '', FIELD_LIMIT.reportClass),
    modeTimeStatis: buildModeTimeStatis(modeData),
    terrainData,
    allModelData,
    modeData,
    gadTable: gadTable || session.gadTable || [],
    phqTable: phqTable || session.phqTable || [],
    modeQuadrantStatis: buildModeQuadrantStatis(modeData),
    workInfo: {
      workName,
      workTime: String(elapsedSec),
      makeNum: String(modeData.length),
      content: session.workContent || '',
      deleteRadio: '0',
      themeType: buildThemeType(modeData, activeWeatherId)
    },
    modelStatistics: buildModelStatistics(modeData)
  }
}

export { SAND_CATEGORY_MAP, FIELD_LIMIT }



// WEBPACK FOOTER //
// ./src/utils/sandTableSave.js